import React, { useState } from 'react';
import type { BusinessType } from '../../../types/venue';
import Button from '../../ui/Button';

export interface AccountData {
  name: string;
  businessType: BusinessType;
  businessTypeOther: string;
  email: string;
  address: string;
  logoFile: File | null;
  logoPreview: string;
}

interface AccountStepProps {
  data: AccountData;
  onUpdate: (data: AccountData) => void;
  onNext: () => void;
}

const BUSINESS_TYPES: { value: BusinessType; label: string }[] = [
  { value: 'coffee_shop', label: 'Coffee Shop' },
  { value: 'bar', label: 'Bar' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'other', label: 'Other' },
];

const inputBase =
  'w-full px-3 py-2.5 border rounded-lg text-sm text-coffee-900 bg-white focus:outline-none focus:ring-2 focus:ring-coffee-500 focus:border-coffee-400 transition-colors';

const AccountStep: React.FC<AccountStepProps> = ({ data, onUpdate, onNext }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!data.name.trim()) newErrors.name = 'Business name is required';
    if (!data.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (data.businessType === 'other' && !data.businessTypeOther.trim()) {
      newErrors.businessTypeOther = 'Please specify your business type';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        onUpdate({
          ...data,
          logoFile: file,
          logoPreview: ev.target?.result as string,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const update = (field: keyof AccountData, value: string) => {
    onUpdate({ ...data, [field]: value });
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-1.5">Step 1 of 5</div>
        <h2 className="text-display text-2xl text-coffee-900">Business basics</h2>
        <p className="mt-1.5 text-sm text-coffee-600">
          Tell us about your business so we can set up your venue page.
        </p>
      </div>

      {/* Business Name */}
      <div>
        <label htmlFor="business-name" className="block text-sm font-medium text-coffee-800 mb-1.5">
          Business name <span className="text-red-500">*</span>
        </label>
        <input
          id="business-name"
          type="text"
          value={data.name}
          onChange={(e) => update('name', e.target.value)}
          placeholder="e.g., Joe's Coffee House"
          className={`${inputBase} ${errors.name ? 'border-red-400' : 'border-coffee-200'}`}
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? 'name-error' : undefined}
        />
        {errors.name && (
          <p id="name-error" className="text-red-500 text-xs mt-1">{errors.name}</p>
        )}
      </div>

      {/* Business Type */}
      <div>
        <label htmlFor="business-type" className="block text-sm font-medium text-coffee-800 mb-1.5">
          Business type <span className="text-red-500">*</span>
        </label>
        <select
          id="business-type"
          value={data.businessType}
          onChange={(e) => update('businessType', e.target.value)}
          className={`${inputBase} border-coffee-200`}
        >
          {BUSINESS_TYPES.map((bt) => (
            <option key={bt.value} value={bt.value}>{bt.label}</option>
          ))}
        </select>
      </div>

      {/* Other type input */}
      {data.businessType === 'other' && (
        <div>
          <label htmlFor="business-type-other" className="block text-sm font-medium text-coffee-800 mb-1.5">
            Specify type <span className="text-red-500">*</span>
          </label>
          <input
            id="business-type-other"
            type="text"
            value={data.businessTypeOther}
            onChange={(e) => update('businessTypeOther', e.target.value)}
            placeholder="e.g., Bakery, Food Truck"
            className={`${inputBase} ${errors.businessTypeOther ? 'border-red-400' : 'border-coffee-200'}`}
            aria-invalid={!!errors.businessTypeOther}
          />
          {errors.businessTypeOther && (
            <p className="text-red-500 text-xs mt-1">{errors.businessTypeOther}</p>
          )}
        </div>
      )}

      {/* Email */}
      <div>
        <label htmlFor="business-email" className="block text-sm font-medium text-coffee-800 mb-1.5">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          id="business-email"
          type="email"
          value={data.email}
          onChange={(e) => update('email', e.target.value)}
          className={`${inputBase} ${errors.email ? 'border-red-400' : 'border-coffee-200'}`}
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? 'email-error' : undefined}
        />
        {errors.email && (
          <p id="email-error" className="text-red-500 text-xs mt-1">{errors.email}</p>
        )}
      </div>

      {/* Address (optional) */}
      <div>
        <label htmlFor="business-address" className="block text-sm font-medium text-coffee-800 mb-1.5">
          Address{' '}
          <span className="text-coffee-400 text-xs font-normal">(optional)</span>
        </label>
        <input
          id="business-address"
          type="text"
          value={data.address}
          onChange={(e) => update('address', e.target.value)}
          placeholder="123 Main St, City, State"
          className={`${inputBase} border-coffee-200`}
        />
      </div>

      {/* Logo (optional) */}
      <div>
        <label className="block text-sm font-medium text-coffee-800 mb-1.5">
          Logo{' '}
          <span className="text-coffee-400 text-xs font-normal">(optional)</span>
        </label>
        <div className="flex items-center gap-4">
          {data.logoPreview && (
            <img
              src={data.logoPreview}
              alt="Logo preview"
              className="w-14 h-14 rounded-xl object-cover border border-coffee-200"
            />
          )}
          <label
            htmlFor="logo-upload"
            className="px-4 py-2 bg-coffee-50 border border-coffee-200 text-coffee-700 text-sm font-medium rounded-lg hover:bg-coffee-100 cursor-pointer transition-colors"
          >
            {data.logoPreview ? 'Change logo' : 'Upload logo'}
          </label>
          <input
            id="logo-upload"
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
            className="hidden"
          />
        </div>
      </div>

      {/* Action row */}
      <div className="flex justify-end pt-2 border-t border-coffee-100">
        <Button variant="primary" onClick={handleNext}>
          Next
        </Button>
      </div>
    </div>
  );
};

export default AccountStep;
